/*
 * Groups the live Payflex, PayJustNow, and Stitch product widgets.
 * Initializes [data-custom-payment-options], supports Shopify section reloads,
 * and is safe to run repeatedly.
 */
(() => {
  const componentSelector = '[data-custom-payment-options]';
  const interactiveSelector = 'a, button, input, select, textarea, [role="button"]';

  function openPayflexModal(event) {
    if (!event.target.closest('.payflex-learn-more-both')) return;

    const modal = document.querySelector('#payflex-modal');
    if (!modal) return;

    event.preventDefault();
    modal.style.display = 'flex';
  }

  function createNormalizedRow(providerNode, provider) {
    if (providerNode.parentElement.querySelector(`[data-custom-payment-display="${provider}"]`)) return true;

    let text = '';
    let logo = null;
    let activate = null;

    if (provider === 'payflex') {
      const amount = providerNode.querySelector('#pay-in-amount')?.textContent.trim();
      const sourceLogo = providerNode.querySelector('#payflex-logo');
      if (!amount || !sourceLogo) return false;

      text = `From ${amount} today, interest & fee free.`;
      logo = sourceLogo.cloneNode(true);
      logo.removeAttribute('id');
      activate = () => {
        const modal = document.querySelector('#payflex-modal');
        if (modal) modal.style.display = 'flex';
      };
    }

    if (provider === 'payjustnow') {
      const sourceText = providerNode.querySelector('.pjn-text p');
      const sourceLogo = providerNode.querySelector('.pjn-logo');
      if (!sourceText || !sourceLogo) return false;

      text = sourceText.innerText.replace('?', '').replace(/\s+/g, ' ').trim();
      logo = sourceLogo.cloneNode(true);
      activate = () => providerNode.querySelector('#pjn-popup-trigger')?.click();
    }

    if (provider === 'stitch') {
      const amount = providerNode.querySelector('.money')?.textContent.trim();
      const sourceLogo = providerNode.querySelector('.bnpl-product-widget > div > div');
      if (!amount || !sourceLogo) return false;

      text = `From ${amount.replace(/\s+/g, '')}, 2–6 interest-free instalments`;
      logo = sourceLogo.cloneNode(true);
      const stitchSvg = logo.querySelector('svg');
      stitchSvg?.setAttribute('viewBox', '0 0 908.152 171');
      stitchSvg?.style.setProperty('height', '58%', 'important');
      stitchSvg?.style.setProperty('width', '58%', 'important');
    }

    const row = document.createElement(activate ? 'button' : 'div');
    row.className = 'custom-payment-options__display';
    row.dataset.customPaymentDisplay = provider;
    if (activate) {
      row.type = 'button';
      row.addEventListener('click', activate);
    }

    const copy = document.createElement('span');
    copy.className = 'custom-payment-options__copy';
    copy.textContent = text;

    const logoWrap = document.createElement('span');
    logoWrap.className = 'custom-payment-options__logo';
    logoWrap.appendChild(logo);

    row.append(copy, logoWrap);
    providerNode.classList.add('custom-payment-options__source');
    providerNode.parentElement.prepend(row);
    return true;
  }

  function normalizeProviders(component) {
    return ['payflex', 'payjustnow', 'stitch'].every((provider) => {
      const providerNode = component.querySelector(`[data-custom-payment-provider="${provider}"]`);
      return providerNode ? createNormalizedRow(providerNode, provider) : false;
    });
  }

  function getProvider(productInfo, component, provider) {
    if (provider === 'payflex') {
      return Array.from(productInfo.querySelectorAll('#payflexCalculatorWidget')).find(
        (node) => !component.contains(node)
      );
    }

    if (provider === 'payjustnow') {
      return Array.from(productInfo.querySelectorAll('.pjn-product')).find(
        (node) => !component.contains(node)
      );
    }

    const stitchWidget = Array.from(productInfo.querySelectorAll('.bnpl-product-widget')).find(
      (node) => !component.contains(node)
    );

    return stitchWidget ? stitchWidget.closest('.shopify-app-block') || stitchWidget : null;
  }

  function moveProvider(component, productInfo, provider) {
    const slot = component.querySelector(`[data-custom-payment-slot="${provider}"]`);
    if (!slot || slot.querySelector(`[data-custom-payment-provider="${provider}"]`)) return true;

    const providerNode = getProvider(productInfo, component, provider);
    if (!providerNode) return false;

    providerNode.setAttribute('data-custom-payment-provider', provider);
    if (provider === 'payflex') providerNode.addEventListener('click', openPayflexModal, true);
    slot.appendChild(providerNode);
    slot.hidden = false;
    return true;
  }

  function moveProviders(component, productInfo) {
    const results = ['payflex', 'payjustnow', 'stitch'].map((provider) =>
      moveProvider(component, productInfo, provider)
    );

    normalizeProviders(component);
    return results.every(Boolean) && normalizeProviders(component);
  }

  function initializeComponent(component) {
    if (component.dataset.customPaymentOptionsInitialized === 'true') return;

    const details = component.querySelector('[data-custom-payment-options-details]');
    const summary = details && details.querySelector(':scope > summary');
    const productInfo = component.closest('product-info');
    if (!details || !summary || !productInfo) return;

    component.dataset.customPaymentOptionsInitialized = 'true';
    let pinnedOpen = false;

    summary.addEventListener('click', (event) => {
      if (event.target.closest(interactiveSelector)) {
        event.stopPropagation();
        return;
      }

      if (event.detail === 0) {
        window.setTimeout(() => {
          pinnedOpen = details.open;
        }, 0);
        return;
      }

      pinnedOpen = !pinnedOpen;
      if (pinnedOpen && details.open) event.preventDefault();
    });

    component.addEventListener('mouseenter', () => {
      details.open = true;
    });

    component.addEventListener('mouseleave', () => {
      if (!pinnedOpen) details.open = false;
    });

    component.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        pinnedOpen = false;
        details.open = false;
        summary.focus();
        return;
      }

      if (!['Enter', ' '].includes(event.key) || event.target !== summary) return;

      event.preventDefault();
      pinnedOpen = !details.open;
      details.open = pinnedOpen;
    });

    if (moveProviders(component, productInfo)) return;

    const observer = new MutationObserver(() => {
      if (moveProviders(component, productInfo)) observer.disconnect();
    });

    observer.observe(productInfo, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 15000);
  }

  function initializePaymentOptions(root = document) {
    if (root.matches && root.matches(componentSelector)) initializeComponent(root);
    root.querySelectorAll(componentSelector).forEach(initializeComponent);
  }

  document.addEventListener('DOMContentLoaded', () => initializePaymentOptions(document));
  document.addEventListener('shopify:section:load', (event) => initializePaymentOptions(event.target));
})();
